import { useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { apiCall } from "utils/apiClient";

const AddExpensesModal = ({ onClose, onSubmit, bookingId, userId }) => {
    const [amount, setAmount] = useState("");
    const [category, setCategory] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("Cash");
    const [transactionId, setTransactionId] = useState("");
    const [vendorOrClient, setVendorOrClient] = useState("");
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        if (!amount || isNaN(amount) || amount <= 0) {
            setError("Please enter a valid amount.");
            setLoading(false);
            return;
        }

        if (!category) {
            setError("Please select a category.");
            setLoading(false);
            return;
        }

        const payload = {
            booking_id: bookingId,
            // user_id: userId,
            amount: parseFloat(amount),
            category,
            payment_method: paymentMethod.toLowerCase(),
            transaction_id: transactionId || `manual_${Date.now()}`,
            vendor_or_client: vendorOrClient,
            description,
        };

        try {
            const result = await apiCall({
                endpoint: "expenses/add",
                method: "POST",
                data: payload,
            });

            if (result && (result.status === 200 || result.success === true)) {
                toast.success("Expense added successfully!");
                onClose();
                onSubmit();
            } else {
                setError(result?.message || "Failed to add expense.");
            }
        } catch (err) {
            setError("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] bg-gray-900/50 flex items-center justify-center p-3 sm:p-4">
            <div className="bg-white p-4 sm:p-5 rounded-lg shadow-lg w-full max-w-xl max-h-[88vh] overflow-y-auto">
                <h3 className="text-lg font-semibold mb-3">Add Expense</h3>
                {error && <p className="text-red-500 text-sm mb-2">{error}</p>}

                <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium mb-1">Expense Amount (Rs)</label>
                            <input
                                type="number"
                                className="w-full h-9 px-3 border rounded-md text-sm"
                                placeholder="Enter amount"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium mb-1">Category</label>
                            <select
                                className="w-full h-9 px-3 border rounded-md text-sm"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                required
                            >
                                <option value="">Select Category</option>
                                <option value="Material">Material</option>
                                <option value="Labour">Labour</option>
                                <option value="Office Supplies">Office Supplies</option>
                                <option value="Travel">Travel</option>
                                <option value="Miscellaneous">Miscellaneous</option>
                                <option value="Other">Other</option>
                                <option value="Vender">Vender</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-medium mb-1">Payment Method</label>
                            <select
                                className="w-full h-9 px-3 border rounded-md text-sm"
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                            >
                                <option value="Cash">Cash</option>
                                <option value="Card">Card</option>
                                <option value="Bank Transfer">Bank Transfer</option>
                                <option value="UPI">UPI</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-medium mb-1">Transaction ID (Optional)</label>
                            <input
                                type="text"
                                className="w-full h-9 px-3 border rounded-md text-sm"
                                placeholder="Enter transaction ID"
                                value={transactionId}
                                onChange={(e) => setTransactionId(e.target.value)}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium mb-1">Vendor/Client</label>
                        <input
                            type="text"
                            className="w-full h-9 px-3 border rounded-md text-sm"
                            placeholder="Enter vendor or client name"
                            value={vendorOrClient}
                            onChange={(e) => setVendorOrClient(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium mb-1">Description</label>
                        <textarea
                            className="w-full px-3 py-2 border rounded-md text-sm min-h-[72px] resize-y"
                            placeholder="Enter description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        ></textarea>
                    </div>

                    <div className="flex justify-end gap-2 pt-1">
                        <button
                            type="button"
                            className="h-9 px-3 bg-gray-400 text-white rounded-md hover:bg-gray-500 text-sm"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="h-9 px-3 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                            disabled={loading}
                        >
                            {loading ? "Adding..." : "Add Expense"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

AddExpensesModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    bookingId: PropTypes.number.isRequired,
    userId: PropTypes.number.isRequired,
};

export default AddExpensesModal;
